import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useImmer } from 'use-immer';
import { useBackendApi } from '../../../helpers/backendHelper';
import { useGlobalState } from '../../../state/GlobalStateProvider';
import { formatDecimals, formatNumber, reviewTimestamp, santizeHTML } from '../../../utils';
import { Flexbox, FlexboxColumn } from '../../action/Flexbox';
import { Line } from '../../action/Line';

import { CHAINS } from '../../../constants';
import Logo from '../../Logo/Logo';
import ProjectTag from '../../ProjectTag/ProjectTag';
import ThemedText from '../../theme/ThemedText';
import ThemeLogo from '../../theme/ThemeLogo';
import ImageAttachments from '../ImageAttachments/ImageAttachments';
import ProjectChip from '../Projectchip/ProjectChip';
import SupertakeChip from '../Projectchip/SupertakeChip';
import CommentWithReadMore from './Comment';
import PostComment from './PostComment';
import Reactions from './Reactions';
import './ReviewCard.scss';
import SupetakeTile from './SupetakeTile';
import Voting from './Voting';

const ReviewCard = forwardRef(({ data, maxWidth = false, showNoOfReviews = true, reactionEligible }, ref) => {
  const [{ user, protocolMapping }] = useGlobalState();
  const localRef = useRef(null);
  const postCommentRef = useRef(null);
  const [openCommentSection, setOpenCommentSection] = useState(false);
  // const [userReaction, setUserReaction] = useState(data.userReaction);
  const backendHelper = useBackendApi();
  const [postData, setPostData] = useImmer(data);
  const [showVoting, setShowVoting] = useState(false);
  const [showProtocolStats, setShowProtocolStats] = useState(false);

  // function handleProjectRedirect(isAirdrop) {
  //   if (isAirdrop) return;
  //   let url;
  //   if (String(postData?.protocol).startsWith('chain')) {
  //     url = `${window.location.origin}/profile/${postData?.userAddress}/chains/${String(postData?.protocol).split('-')[1]}`;
  //   } else {
  //     url = `${window.location.origin}/profile/${postData?.userAddress}/projects/${postData?.protocol}`;
  //   }
  //   window.open(url, '_blank');
  // }

  function handleProtocol(project) {
    if (String(project).startsWith('chain')) {
      const chainId = Number(String(project).split('-')?.[1]);
      return CHAINS[chainId];
    } else {
      return protocolMapping[project];
    }
  }

  const handleOpenCommentSection = () => {
    setOpenCommentSection(!openCommentSection);
    postCommentRef?.current?.expand();
  };

  async function deleteComment(commentId) {
    if (!commentId) return;

    try {
      await backendHelper.reviews.deleteComment(commentId);
      setPostData(state => {
        state.comments = [];
      });
      alert.success('Comment deleted');
    } catch (error) {
      alert.error('Something went wrong!');
    }
  }

  function handleRedirect(address) {
    const url = `${window.location.origin}/profile/${address}/projects`;
    window.open(url, '_blank');
  }

  useEffect(() => {
    if (!ref) return;

    if (typeof externalRef === 'function') {
      ref(localRef.current);
    } else {
      ref.current = localRef.current;
    }
  }, [ref]);

  return (
    <FlexboxColumn ref={localRef} className={`reviewcard_wrapper ${maxWidth && 'review_wrapper_width'}`} gap='1.5rem'>
      <Flexbox justifyContent='space-between' fw>
        <Flexbox gap='0.5rem' className='reviewcard_wrapper_user' onClick={() => handleRedirect(postData?.userAddress)}>
          <Logo src={postData?.userAvatarUrl} width={'1.5rem'} height={'1.5rem'} borderRadius='50%' />
          <Flexbox gap='0.5rem' className='reviewcard_wrapper_user_name'>
            <ThemedText.H6 weight='medium'>{postData?.userName}</ThemedText.H6>
        
            {postData?.superTake ? <SupertakeChip project={postData?.protocol} tags={postData?.tags} /> : <ProjectChip project={postData?.protocol} tags={postData?.tags} />}
          </Flexbox>

          <ThemedText.H6 className='reviewcard_wrapper_light'>{reviewTimestamp({ timestamp: parseInt(postData?.timestamp) })}</ThemedText.H6>
        </Flexbox>
        {postData?.superTake ? (
          <ThemeLogo className={'pointer'} onClick={() => setShowProtocolStats(!showProtocolStats)} icon={'supertakeYellow'} />
        ) : (
          <ThemedText.H6 color='color-xlight'>
            ${formatNumber(formatDecimals(postData?.userVol))} vol on {handleProtocol(postData?.protocol)?.name}
          </ThemedText.H6>
        )}
      </Flexbox>
      {showProtocolStats && <SupetakeTile data={postData?.data} />}

      <div className='reviewcard_wrapper_review'>
        {postData?.header && <ThemedText.H4 weight='medium'>{postData?.header}</ThemedText.H4>}
        <div dangerouslySetInnerHTML={{ __html: santizeHTML(postData?.content) }} />
        <ImageAttachments attachments={postData?.attachments} />
      </div>
      {/* <Line /> */}
      {postData?.comments?.length > 0 && (
        <>
          <FlexboxColumn fw gap='0.75rem'>
            <Flexbox justifyContent='space-between' fw>
              <Flexbox gap='0.5rem' className='reviewcard_wrapper_user' onClick={() => handleRedirect(postData?.comments?.[0]?.user?.address)}>
                <Logo src={postData?.comments?.[0]?.user?.avatarUrl} width={'1.5rem'} height={'1.5rem'} borderRadius='50%' />
                <ThemedText.H6 weight='medium'>{postData?.comments?.[0]?.user?.name}</ThemedText.H6>
                <ProjectTag project={postData?.protocol} />
                <ThemedText.H6 className='reviewcard_wrapper_light'>replied</ThemedText.H6>
              </Flexbox>
              {user?.address === postData?.comments?.[0]?.user?.address && postData?.comments?.[0]?.commentid && (
                <ThemedText.H6 onClick={() => deleteComment(postData?.comments?.[0]?.commentid)} className='reviewcard_wrapper_light pointer'>
                  delete reply
                </ThemedText.H6>
              )}
            </Flexbox>
            <div className='reviewcard_wrapper_review'>
              <CommentWithReadMore content={postData?.comments?.[0]?.content} />
              <ImageAttachments attachments={data?.attachments} />
            </div>
          </FlexboxColumn>
          <Line />
        </>
      )}
      <Reactions postData={postData} handlePostData={setPostData} eligible={reactionEligible?.has(postData?.protocol)} />
      {/* <Flexbox justifyContent='space-between' fw>
        <Flexbox fw justifyContent='flex-start'>
          {' '}
          {REACTIONS.map(reaction => (
            <button
              key={reaction.key}
              className={`review_action_button ${reaction.key === userReaction && 'review_action_button_active'} ${postData?.superTake && 'review_action_button_supertake'}`}
              onClick={() => postReaction(reaction?.key)}
            >
              <ThemeLogo icon={reaction.logo} />
              <ThemedText.H6>
                {reaction.name} <ThemedText.Span className='reviewcard_wrapper_light'> ({handleReactionCount(reaction.key)})</ThemedText.Span>{' '}
              </ThemedText.H6>
            </button>
          ))}
        </Flexbox>

        {postData?.comments?.length === 0 && protocolAdmin?.has(postData?.protocol) ? (
          <button className='review_action_button' onClick={handleOpenCommentSection}>
            <ThemeLogo icon={'comment'} /> <ThemedText.H6>Comment</ThemedText.H6>
          </button>
        ) : (
          <div></div>
        )}
      </Flexbox> */}

      {openCommentSection && <PostComment ref={postCommentRef} updateComment={setPostData} postId={postData?.postId} handleClose={handleOpenCommentSection} />}

      <Flexbox onClick={() => setShowVoting(!showVoting)} className='pointer'>
        <ThemedText.H6>Who's Voting</ThemedText.H6>
        {showVoting ? <ThemeLogo icon={'blueArrowUp'} /> : <ThemeLogo icon={'blueArrowDown'} />}
      </Flexbox>
      {showVoting && <Voting postData={postData} />}
    </FlexboxColumn>
  );
});

export default ReviewCard;
